import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { LeadersFilters, TabulationPermission, User } from '../../../types';
import React, { useEffect, useState } from 'react';
import {
  fetchBarangaysByMunicipality,
  fetchMunicipalitiesByDistrict
} from '../../../utils/locationUtils';

import useAuth from '../../../hooks/useAuth';

interface LeadersFormProps {
  onGenerateReport: (filters: LeadersFilters) => void;
}

const LeadersForm: React.FC<LeadersFormProps> = ({ onGenerateReport }) => {
  const { user } = useAuth();
  
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [barangays, setBarangays] = useState<string[]>([]);

  const [filters, setFilters] = useState<LeadersFilters>({
    region: 'REGION XIII',
    province: 'SURIGAO DEL NORTE',
    district: 'DISTRICT 1',
    municipality: '',
    barangay: ''
  });

  useEffect(() => {
    const loadInitialData = async (user: User) => {
      try {
        const districtMunicipalities = await fetchMunicipalitiesByDistrict(
          filters.region, 
          filters.province, 
          filters.district
        );

        // Filter municipalities based on user's allowedTabulations if set
        const filteredMunicipalities = user.allowedTabulations 
          ? districtMunicipalities.filter(municipality => 
              user.allowedTabulations?.some(
                (permission: TabulationPermission) => 
                  permission.type === 'municipal' && permission.id === municipality
              )
            )
          : districtMunicipalities;

        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    if (user) {
      loadInitialData(user);
    }
  }, [user]);

  const handleMunicipalityChange = async (municipality: string) => {
    try {
      const newBarangays = await fetchBarangaysByMunicipality(
        filters.region, 
        filters.province, 
        filters.district, 
        municipality
      );
      setBarangays(newBarangays);
      setFilters(prev => ({
        ...prev,
        municipality,
        barangay: '',
        precinct: '',
        candidateFilters: [{
          position: '',
          candidate: '',
          matchType: 'match'
        }]
      }));
    } catch (error) {
      console.error('Error fetching barangays:', error);
    }
  };

  const handleBarangayChange = async (barangay: string) => {
    try {
      setFilters(prev => ({
        ...prev,
        barangay,
      }));
    } catch (error) {
      console.error('Error fetching precincts:', error);
    }
  };

  const handleSubmit = () => {
    // Validate mandatory fields
    if (!filters.region || !filters.province || !filters.district || 
        !filters.municipality || !filters.barangay) {
      alert('Please fill in all required fields');
      return;
    }

    onGenerateReport(filters);
  };

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {/* Municipality Dropdown */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Municipality *</InputLabel>
          <Select
            value={filters.municipality}
            label="Municipality *"
            onChange={(e) => handleMunicipalityChange(e.target.value)}
            disabled={!filters.district}
            required
          >
            {municipalities.map(municipality => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Barangay Dropdown */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Barangay *</InputLabel>
          <Select
            value={filters.barangay}
            label="Barangay *"
            onChange={(e) => handleBarangayChange(e.target.value)}
            disabled={!filters.municipality}
            required
          >
            {barangays.map(barangay => (
              <MenuItem key={barangay} value={barangay}>
                {barangay}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Submit Button */}
      <Grid item xs={12}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={!filters.region || !filters.province || 
                    !filters.district || !filters.municipality || 
                    !filters.barangay}
        >
          Generate Report
        </Button>
      </Grid>
    </Grid>
  );
};

export default LeadersForm;
